import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Link, ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import OTPInput, { OTPInputRef } from "../../components/OTPInput";
import useRequestResumeOtp from "../../hooks/useRequestResumeOtp";
import useVerifyResumeOtp from "../../hooks/useVerifyResumeOtp";
import SignUpNavbar from "../../components/SignUpNavbar";
import { useAuth } from "../../context/AuthContext";

type ResumeProfileLocationState = {
  email?: string;
};

/**
 * Resume Profile OTP screen.
 * Shown when a user verified their email but closed the browser before
 * setting a password. This screen sends them a fresh OTP so they can
 * get a new JWT and continue to the Create Password step.
 */
const ResumeProfileOtp = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { refetch } = useAuth();

  const [email, setEmail] = useState("");
  const [otpRequested, setOtpRequested] = useState(false);
  const [timer, setTimer] = useState(60);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [otp, setOTP] = useState("");

  const otpInputRef = useRef<OTPInputRef | null>(null);

  // --- Redirect guard: must arrive with an email in route state ---
  useEffect(() => {
    const s = state as ResumeProfileLocationState | null;
    if (s?.email) {
      setEmail(s.email);
    } else {
      navigate("/sign-up", { replace: true });
    }
  }, [state, navigate]);

  // --- Request OTP on mount (once we have the email) ---
  const {
    requestResumeOtp,
    requesting,
    isRequestError,
    requestError,
    resetRequest,
  } = useRequestResumeOtp();

  useEffect(() => {
    if (email && !otpRequested) {
      requestResumeOtp(
        { email },
        {
          onSuccess() {
            setOtpRequested(true);
            toast.success("We sent a verification code to your email.");
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // --- Verify OTP ---
  const {
    verifyResumeOtp,
    verifying,
    isVerifyError,
    verifyError,
    resetVerify,
  } = useVerifyResumeOtp({
    async onSuccess(data) {
      if (data.data?.token) {
        localStorage.setItem("token", data.data.token);
        refetch();
        navigate("/sign-up/create-password", { replace: true });
      }
    },
  });

  // --- Countdown timer ---
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setResendEnabled(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // --- Derived state ---
  const isValid = useMemo(() => {
    return (
      otp.length === 4 &&
      otp.split("").every((v) => v !== "" && !isNaN(Number(v)))
    );
  }, [otp]);

  const isLoading = requesting || verifying;

  const isError = isRequestError || isVerifyError;
  const error = verifyError || requestError;

  // --- Handlers ---
  const resetAll = useCallback(() => {
    resetRequest();
    resetVerify();
    otpInputRef.current?.reset();
  }, [resetRequest, resetVerify]);

  const handleResend = useCallback(() => {
    resetAll();
    requestResumeOtp(
      { email },
      {
        onSuccess() {
          toast.success("We sent a new code to your email.");
          setOTP("");
          setTimer(60);
          setResendEnabled(false);
        },
      }
    );
  }, [requestResumeOtp, email, resetAll]);

  const handleSubmit = useCallback(() => {
    if (!isValid || isLoading) return;
    verifyResumeOtp({ email, code: otp });
  }, [isValid, isLoading, verifyResumeOtp, email, otp]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <div>
      <ScrollRestoration />
      {isLoading && (
        <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader color="#EA5C29" />
        </div>
      )}
      <div>
        <SignUpNavbar />
        <main className="flex min-h-[calc(100vh-4rem)] flex-col justify-center items-center py-12 px-5 md:px-12 lg:px-20 gap-8 text-lg">
          <div className="p-5 bg-gray-100 rounded-full" aria-hidden>
            <EnvelopeSimple size={30} className="text-deepBlue" />
          </div>
          <div className="text-center max-w-lg space-y-2">
            <h1 className="text-2xl font-bold text-deepBlue">
              Resume your registration
            </h1>
            <p className="text-gray-600">
              {email ? (
                <>
                  We sent a 4-digit code to{" "}
                  <span className="font-medium text-deepBlue">{email}</span> so
                  you can continue setting up your account.
                </>
              ) : (
                "Enter the 4-digit code we sent to your email."
              )}
            </p>
            <p className="text-sm text-gray-500">
              Code expires in a few minutes. Check spam or promotions if you
              don&apos;t see it.
            </p>
          </div>
          <form
            onSubmit={handleFormSubmit}
            className="w-full flex-col gap-6 flex items-center justify-center text-base max-w-md"
            noValidate
          >
            <OTPInput
              ref={otpInputRef}
              onChange={(next) => setOTP(next)}
              length={4}
            />
            {!resendEnabled && (
              <p className="text-gray-500 text-sm" aria-live="polite">
                Resend code in {timer}s
              </p>
            )}
            {resendEnabled && (
              <p className="text-gray-500 text-sm text-center">
                Didn&apos;t get a code?{" "}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={requesting}
                  className="font-semibold text-visaro-orange underline underline-offset-2 hover:text-deepBlue disabled:opacity-50"
                >
                  Resend code
                </button>
              </p>
            )}
            {isError && (
              <div
                role="alert"
                className="text-red-600 text-center text-sm w-full rounded-lg bg-red-50 border border-red-100 px-4 py-3"
              >
                {error?.message ?? "Something went wrong. Try again."}
              </div>
            )}
            <button
              type="submit"
              disabled={!isValid || isLoading}
              className="p-4 px-5 w-full border-visaro-gray-700 border rounded-xl font-bold bg-deepBlue text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-deepBlue/90 transition-colors"
            >
              Continue
            </button>
          </form>
          <div className="flex flex-col gap-3 items-center text-sm text-gray-600">
            <Link
              to="/sign-up"
              className="text-visaro-orange font-medium hover:underline"
            >
              Use a different email
            </Link>
            <Link to="/login" className="text-gray-600 hover:text-deepBlue">
              Back to <span className="text-visaro-orange">Log in</span>
            </Link>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ResumeProfileOtp;
